import PropTypes from "prop-types";
import { useContext, useState } from "react";
import Modal from "react-modal";
import GuiContext from "config/context/guiContext";
import ToastContext from "config/context/toastContext";
import useCustomStyles from "config/useCustomStyles";
import useTheme from "config/useTheme";
import styles from "css/privateKey.module.css";
import styles2 from "css/addNewDatabase.module.css";
import Button from "../shared/button/Button";
import PrivateKeyField from "components/shared/private-key-field/PrivateKeyField";
import Irreversible from "components/shared/status/Irreversible";
import { useShareAccess } from "features/compositions/services/useShareAccess";

/**
 * Ko sve sme da koristi bazu.
 *
 * Zaseban prozor, ne još jedna grana u `AddNewAccount`: taj već nosi šest
 * tokova kroz jedan `switch`, a ovaj ni sa jednim ne deli korake.
 *
 * Tri ishoda, i razlika među njima je koje ovlašćenje se dira:
 *
 *   share     primalac dobija svoj ključ na `active`; `owner` ostaje tvoj,
 *             pa možeš da opozoveš bez njegovog pristanka
 *   revoke    njegov ključ silazi sa `active`
 *   handover  i `owner` i `active` postaju samo njegovi — jedini nepovratan
 *
 * Uvek se traži GUIName, nikad ključ: javni ključ se čita sa lanca, pa se tuđi
 * ključevi nigde ne kucaju. Ono što se ne može proveriti je pogrešno unet
 * guiname koji stvarno postoji — zato korak potvrde pokazuje čiji je to nalog i
 * koji mu je javni ključ, pre nego što se bilo šta pošalje.
 */
const MODES = {
  share: {
    title: "Give access",
    button: "Give access",
    danger: false,
  },
  revoke: {
    title: "Take access away",
    button: "Take access away",
    danger: false,
  },
  handover: {
    title: "Hand it over completely",
    button: "Hand it over",
    danger: true,
  },
};

const ShareAccess = ({ openModal, setOpenModal }) => {
  const { dark } = useTheme();
  const customStyles = useCustomStyles();
  const { insertIntoToastList } = useContext(ToastContext);
  const { userData, unlinkDatabase, linkDatabase } = useContext(GuiContext);

  const [database, setDatabase] = useState("");
  const [recipient, setRecipient] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [mode, setMode] = useState("share");
  const [step, setStep] = useState("start");
  const [confirmName, setConfirmName] = useState("");

  const { check, share, checking, sharing, result, error, done, reset } = useShareAccess();

  const close = () => {
    setDatabase("");
    setRecipient("");
    setPrivateKey("");
    setMode("share");
    setStep("start");
    setConfirmName("");
    reset();
    setOpenModal(false);
  };

  const onContinue = async () => {
    const found = await check(database, recipient, privateKey);
    if (found) setStep("confirm");
  };

  const onConfirm = async () => {
    const ok = await share(database, recipient, privateKey, mode);
    if (!ok) return;
    // Spisak u pregledaču se poravnava sa onim što je transakcija upravo
    // uradila na lancu — inače bi baza ostala prikazana kod nekoga ko nad njom
    // više nema prava, do sledeće prijave.
    if (mode === "handover") unlinkDatabase(database);
    if (mode === "share" && recipient === userData?.guiname) linkDatabase(database);
    insertIntoToastList(ok.message, "success", "shareAccess");
    setStep("done");
  };

  // Matična baza (prva u spisku) se ne predaje: to bi bilo davanje sopstvenog
  // identiteta. Deljenje i opoziv nad njom su i dalje u redu — samo handover ne.
  // Backend odbija isto; ovo je da se razlog vidi pre potvrde.
  const handoverPrimary =
    mode === "handover" &&
    Boolean(database) &&
    userData?.accounts?.[0] === database;

  const ready =
    database &&
    recipient &&
    privateKey &&
    recipient !== userData?.guiname &&
    !handoverPrimary &&
    !checking;

  const renderStart = () => (
    <>
      <div className={styles.text}>
        <h2>Share Access</h2>
      </div>
      <input
        value={database}
        onChange={(e) => setDatabase(e.target.value)}
        type="text"
        placeholder="Enter database name"
      />
      <input
        value={recipient}
        onChange={(e) => setRecipient(e.target.value)}
        type="text"
        placeholder="Enter the other user's GUI name"
      />
      <PrivateKeyField
        value={privateKey}
        onChange={(e) => setPrivateKey(e.target.value)}
        placeholder="Enter your private key"
      />

      <ul className={styles2?.warning || ""}>
        {Object.entries(MODES).map(([key, m]) => (
          <li key={key}>
            <label>
              <input
                type="radio"
                name="share_mode"
                checked={mode === key}
                onChange={() => setMode(key)}
              />{" "}
              <b>{m.title}</b>
              {key === "share" && " — they get their own key. Yours stays, and you can take it back at any time without asking them."}
              {key === "revoke" && " — their key comes off. They keep nothing, and the database drops off their list."}
              {key === "handover" && " — the database becomes theirs alone. You lose access and it leaves your list. This one cannot be undone."}
            </label>
          </li>
        ))}
      </ul>

      <p className={styles2?.warning || ""}>
        Only the owner key of a database can change who reaches it. A key that
        was shared with you can read and write, but not pass it on.
      </p>

      {recipient && recipient === userData?.guiname && (
        <p className={styles.error}>That is your own account.</p>
      )}

      {handoverPrimary && (
        <p className={styles.error}>
          {database} is your main database — the account you registered with. It
          cannot be handed over. You can still share it or revoke access.
        </p>
      )}
      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.btnGroup}>
        <Button disabled={!ready} onClick={onContinue}>
          {checking ? "Checking…" : "Continue"}
        </Button>
        <Button buttonType="text" variant="secondary" onClick={close}>
          Cancel
        </Button>
      </div>
    </>
  );

  const renderConfirm = () => {
    const m = MODES[mode];
    if (!result?.recipient_exists) {
      return (
        <>
          <div className={styles.text}>
            <h2>No such user</h2>
          </div>
          <p className={styles.error}>{result?.message}</p>
          <p className={styles2?.warning || ""}>
            A GUI name is not a chain account name. Ask them for the name they
            log in with.
          </p>
          <div className={styles.btnGroup}>
            <Button onClick={() => { reset(); setStep("start"); }}>Back</Button>
            <Button buttonType="text" variant="secondary" onClick={close}>Cancel</Button>
          </div>
        </>
      );
    }

    return (
      <>
        <div className={styles.text}>
          <h2>{m.title}</h2>
        </div>

        {/* Ime, nalog i ključ zajedno. Guiname koji postoji a nije taj koga si
            mislio je jedina omaška koju provera ne hvata — jedino što protiv
            nje pomaže je da se primalac vidi pre slanja. */}
        <ul className={styles2?.warning || ""}>
          <li><b>Database</b> — {database}</li>
          {/* GUI ime, ono što je korisnik i uneo. Nalog na koji ključ ide je
              unutrašnja stvar — pošiljaocu ne znači ništa, a `recipient_account`
              od 31.08. nosi baš taj nalog, pa bi ovde stajalo tuđe ime. */}
          <li><b>Goes to</b> — {recipient}</li>
          {/* Ranije je pisalo „not readable from the chain" kad god ključ
              izostane. To je krivicu bacalo na lanac, a ključ je bio čitljiv —
              samo se tražio na pogrešnom mestu. Razlog sada stiže sa servera. */}
          <li>
            <b>Their key</b> —{" "}
            {result.recipient_key || result.recipient_problem || "could not be determined"}
          </li>
          {/* Server je ovo vraćao od 31.08. a niko ga nije prikazivao. Ono što
              kaže je različito po režimu, i u oba slučaja menja odluku:
                share    već je na spisku -> ovo ne dodaje ništa novo
                revoke   NIJE na spisku   -> oduzimaš nekome ko bazu ne vidi
              Zato se ne piše kao gola vrednost nego kao posledica. */}
          <li>
            <b>On their list</b> —{" "}
            {result.recipient_lists_it
              ? mode === "revoke"
                ? "yes — it will drop off it"
                : "yes, already"
              : mode === "revoke"
                ? "no — they do not see this database at all"
                : "no — it will appear there"}
          </li>
        </ul>

        {!result.key_valid && (
          <p className={styles.error}>
            That private key does not match {database}. Nothing can be changed
            without it.
          </p>
        )}

        {mode === "handover" && (
          <>
            <p className={styles.error}>
              After this you cannot read, write or delete {database}, and it
              leaves your list. Only {recipient} will be able to
              give it back.
            </p>
            <input
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              type="text"
              placeholder={`Type ${database} to confirm`}
            />
          </>
        )}

        {mode === "share" && (
          <p className={styles2?.warning || ""}>
            They will be able to read and write every table, and to empty or
            delete the database. Sharing does not divide the data — it adds
            someone who can change it.
          </p>
        )}

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.btnGroup}>
          <Button
            variant={m.danger ? "danger" : undefined}
            // Bez primaočevog ključa se ovo ne može izvesti, a do 31.08. je
            // dugme svejedno bilo aktivno: slanje bi palo unutar sortiranja
            // ključeva, uz poruku koja ne pominje ni primaoca ni razlog.
            disabled={
              sharing ||
              !result.key_valid ||
              !result.recipient_key ||
              (mode === "handover" && confirmName !== database)
            }
            disabledReason={
              !result.recipient_key
                ? result.recipient_problem || "The recipient's key could not be determined."
                : !result.key_valid
                  ? `That private key does not match ${database}.`
                  : mode === "handover" && confirmName !== database
                    ? `Type ${database} above to confirm.`
                    : undefined
            }
            onClick={onConfirm}
          >
            {sharing ? "Working…" : m.button}
          </Button>
          <Button buttonType="text" variant="secondary" disabled={sharing} onClick={() => { reset(); setStep("start"); }}>
            Back
          </Button>
          <Button buttonType="text" variant="secondary" disabled={sharing} onClick={close}>
            Cancel
          </Button>
        </div>
      </>
    );
  };

  const renderDone = () => (
    <>
      <div className={styles.text}>
        <h2>{done?.message}</h2>
      </div>
      {mode === "share" && (
        // Ranije je ovde pisalo da GUI neće nuditi pražnjenje ni brisanje dok
        // baza ima dva ključa. Ta brana je 01.09. skinuta u celini (tačka C),
        // pa je poruka obećavala zaštitu koje više nema.
        <p className={styles2?.warning || ""}>
          {database} now has two keys. Either of you can empty or delete it —
          the chain knows no lesser right than a key. Take the key back with
          Take access away if that is not what you meant.
        </p>
      )}
      {/* Samo predaja. Ona jedina ne može da se povuče, pa je jedina kod koje
          razlika između „zapisano" i „nepovratno" menja šta korisnik radi. */}
      {mode === "handover" && <Irreversible blockNum={done?.block_num} />}
      <div className={styles.btnGroup}>
        <Button onClick={close}>OK</Button>
      </div>
    </>
  );

  return (
    <Modal isOpen={openModal} ariaHideApp={false} style={customStyles} contentLabel="Modal">
      <div className={`${styles.privateKey} ${dark ? styles.dark : ""}`} style={{ position: "relative" }}>
        {step === "start" && renderStart()}
        {step === "confirm" && renderConfirm()}
        {step === "done" && renderDone()}
      </div>
    </Modal>
  );
};

ShareAccess.propTypes = {
  openModal: PropTypes.bool.isRequired,
  setOpenModal: PropTypes.func.isRequired,
};

export default ShareAccess;
